import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { THEME_LABELS } from '@/lib/company-qualification';
import { linesById, pageIsStale } from '@/lib/company-restitution';
import { QualificationReadView } from '@/components/profile/QualificationReadView';
import { ThemePageView } from '@/components/profile/restitution/ThemePageView';
import { type RestitutionWriting, writingStatus } from '@/components/profile/restitution/RestitutionView';
import {
  type CompanyQualificationSheet,
  QUALIFICATION_THEMES,
  type QualificationThemeKey,
} from '@shared/types/company-qualification';

interface Props {
  sheet: CompanyQualificationSheet;
  theme: QualificationThemeKey;
  writing: RestitutionWriting | null;
  sheetDirty: boolean;
  onBack: () => void;
  onDeepen: () => void;
  /** Writes (or rewrites) this theme's page from its lines. */
  onWrite: () => void;
  onLines: () => void;
}

/**
 * One theme on its own: its page with the visual that fits it (table, funnel, steps,
 * scorecard, cards) once written; before that, its lines and how to get the page.
 */
export const ThemeFocusView = ({ sheet, theme, writing, sheetDirty, onBack, onDeepen, onWrite, onLines }: Props) => {
  const page = sheet.restitution.pages[theme];
  const sections = QUALIFICATION_THEMES.find(({ key }) => key === theme)?.sections ?? [];
  const lines = sections.reduce((total, section) => total + sheet.sections[section].length, 0);
  const running = writing?.running ?? false;
  const canWrite = lines > 0 && (!page || pageIsStale(sheet, theme));
  return (
    <div className="min-w-0 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-domino-inkSoft hover:text-domino-ink">
          <ArrowLeft className="h-4 w-4" aria-hidden /> Tous les thèmes
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {canWrite && (
            <Button size="sm" variant="secondary" onClick={onWrite} loading={running} disabled={running || sheetDirty}>
              {page ? 'Mettre à jour la page' : 'Rédiger la page'}
            </Button>
          )}
          <Button size="sm" onClick={onDeepen}>Approfondir avec l’analyste</Button>
        </div>
      </div>
      {running && <p className="text-sm text-domino-inkSoft">{writingStatus(writing!)}</p>}
      {page ? (
        <ThemePageView theme={theme} page={page} lines={linesById(sheet)} stale={pageIsStale(sheet, theme)} onDeepen={onDeepen} onLines={onLines} />
      ) : lines > 0 ? <QualificationReadView sheet={sheet} focused={theme} /> : (
        <div className="rounded-xl border border-domino-cardBorder bg-domino-warm px-6 py-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-domino-inkSoft">{THEME_LABELS[theme].label}</p>
          <p className="mt-1 font-display text-xl leading-snug text-domino-ink">{THEME_LABELS[theme].deliverable}</p>
        </div>
      )}
    </div>
  );
};
